import React, { useCallback, useMemo, useState } from "react";

import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { Box, Chip, Stack, Typography } from "@mui/material";
import { SyncIcon, VisibilityLock } from "assets/material-custom-icons";
import { FileIcon } from "components/old-data-box-components/File-icon/FileIcon";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { EntityType, RoleName } from "constants/index";
import { useGetGroupNames, useIsViewerPermission } from "hooks";
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_Row,
  MRT_SortingState,
  useMaterialReactTable
} from "material-react-table";
import { useUserRoleSelector } from "store";
import { SyncEntityTagsData } from "store/actions/deal-actions/tag-entity-actions";
import {
  FolderKind,
  IDataroomFileDetail,
  IDocumentMetaDetail,
  IFileMetaDetail,
  IFolderMetaDetail,
  IFormMetaDetail,
  IMetaDetails
} from "store/types";
import { formatDate } from "utils";

import { useUpdateCellHook } from "../Hooks/use-update-cell-hook";
import { DocumentActions, Props as DocumentActionsProps } from "../Menus/DocumentActions";
import { FileActions, Props as FileActionsProps } from "../Menus/FileActions";
import { FolderActions, Props as FolderActionsProps } from "../Menus/FolderActions";
import { FormActions, Props as FormActionsProps } from "../Menus/FormActions";
import { AddValueButton } from "./Cells/AddValueCell";
import { DealCell } from "./Cells/DealCell";
import { EditableCell } from "./Cells/EditableCell";
import { getStatusConfig } from "./Cells/Prompts/OperationslStatusPrompt";
import { SharedCell } from "./Cells/SharedCell";
import { TagsCell } from "./Cells/TagsCell";
import { TextCell } from "./Cells/TextCell";

const tableBodyRowProps = {
  sx: {
    backgroundColor: "white",
    height: "32px",
    minHeight: "32px",
    "& td": { verticalAlign: "middle", height: "32px", minHeight: "32px" },
    "&:hover": { backgroundColor: "#ecf1f8 !important" },
    "&:hover td": { backgroundColor: "#ecf1f8 !important" },
    "&:hover td::after": { backgroundColor: "#ecf1f8" },
    "&:hover .row-hover-actions": { opacity: 1 },
    "&:hover .pinned-actions-content": { opacity: 1, pointerEvents: "auto" as const },
    "&:has([aria-expanded='true'])": { backgroundColor: "#ecf1f8 !important" },
    "&:has([aria-expanded='true']) td": { backgroundColor: "#ecf1f8 !important" },
    "&:has([aria-expanded='true']) td::after": { backgroundColor: "#ecf1f8" },
    "&:has([aria-expanded='true']) .row-hover-actions": { opacity: 1 },
    "&:has([aria-expanded='true']) .pinned-actions-content": { opacity: 1, pointerEvents: "auto" as const },
    "&:last-child td": { borderBottom: "none" },
    "&.Mui-selected td": { backgroundColor: "white !important" }
  }
};

interface Props {
  data: IMetaDetails[];
  isLoading?: boolean;
  columnVisibility?: Record<string, boolean>;
  columnOrder?: string[];
  onRowClick?: (row: IMetaDetails) => void;
  kind: FolderKind;
  selectedItems?: IMetaDetails[];
  setSelectedItems?: (items: IMetaDetails[]) => void;
  reorderColumns?: (order: string[]) => void;
  refetchFolderMetadata?: () => Promise<void>;
  getRowDragHandlers?: (row: IMetaDetails) => {
    isHighlighted: boolean;
    draggable: boolean;
    onDragStart?: React.DragEventHandler<HTMLTableRowElement>;
    onDragEnd?: React.DragEventHandler<HTMLTableRowElement>;
    onDragOver?: React.DragEventHandler<HTMLTableRowElement>;
    onDragLeave?: React.DragEventHandler<HTMLTableRowElement>;
    onDrop?: React.DragEventHandler<HTMLTableRowElement>;
  };
  enableRowSelection?: boolean;
  hideShareColumn?: boolean;
  FolderActionsComponent?: React.ComponentType<FolderActionsProps>;
  FormActionsComponent?: React.ComponentType<FormActionsProps>;
  DocumentActionsComponent?: React.ComponentType<DocumentActionsProps>;
  FileActionsComponent?: React.ComponentType<FileActionsProps>;
  useUpdateCellHookOverride?: typeof useUpdateCellHook;
  /** Show the "Deal" column (used in global files view). */
  showDealColumn?: boolean;
  /** Called when the user scrolls near the bottom — used for infinite scroll. */
  onFetchNextPage?: () => void;
  /** When true, sorting is handled server-side (disables client sort). */
  manualSorting?: boolean;
  /** Callback when sorting changes. */
  onSortingChange?: (sorting: { sort_by: string; sort: "asc" | "desc" } | null) => void;
  /** Show dataroom-specific columns regardless of folder kind (used in All Files / Recently Added). */
  showDataroomColumns?: boolean;
  /** ID of the row to highlight (e.g., after closing data extractor). */
  highlightedItemId?: number | null;
}

/** Maps MRT column ids to server-accepted sort_by values. */
const SORT_COLUMN_MAP: Record<string, string> = {
  name: "name",
  deal: "deal_title",
  altius_file_classification: "document_type",
  dataroom_classification: "dataroom_classification",
  investor: "investor",
  account_name: "account_name",
  file_status: "file_status",
  tags: "tags",
  shared_with: "shared_with",
  type: "file_type",
  last_update: "last_update",
  effective_date: "effective_date",
  currency: "currency",
  source: "source",
  upload_date: "created_at",
  dataroom_sync_date: "downloaded_at",
  dataroom_index_date: "created_at",
  dataroom_effective_date: "effective_at"
};

const READ_ONLY_ROLES = [RoleName.Guest, RoleName.Viewer, RoleName.TeamViewer];

export const MetadataTable: React.FC<Props> = ({
  data,
  isLoading = false,
  columnVisibility = {},
  columnOrder,
  onRowClick,
  kind,
  selectedItems = [],
  setSelectedItems = () => {},
  reorderColumns = () => {},
  refetchFolderMetadata = () => {
    return Promise.resolve();
  },
  getRowDragHandlers,
  enableRowSelection = true,
  hideShareColumn = false,
  FolderActionsComponent = FolderActions,
  FormActionsComponent = FormActions,
  DocumentActionsComponent = DocumentActions,
  FileActionsComponent = FileActions,
  useUpdateCellHookOverride = useUpdateCellHook,
  showDealColumn = false,
  onFetchNextPage,
  manualSorting = false,
  onSortingChange,
  showDataroomColumns = false,
  highlightedItemId = null
}) => {
  const {
    handleClassificationUpdate,
    handleInvestorUpdate,
    handleEffectiveDateUpdate,
    handleCurrencyUpdate,
    handleOperationsStatusUpdate,
    handleShareUpdate,
    handleTagsSync
  } = useUpdateCellHookOverride(refetchFolderMetadata);

  const getGroupNames = useGetGroupNames();

  const role = useUserRoleSelector();
  const isAnalystDealsViewerPermission = useIsViewerPermission("deals");

  const isReadOnly =
    isAnalystDealsViewerPermission || Boolean(role?.name && READ_ONLY_ROLES.includes(role.name as RoleName));

  const rowSelection = useMemo<Record<string, boolean>>(() => {
    const map: Record<string, boolean> = {};
    selectedItems.forEach((item) => {
      const idx = data.findIndex((d) => d.id === item.id && d.type === item.type);
      if (idx !== -1) map[String(idx)] = true;
    });
    return map;
  }, [selectedItems, data]);

  const handleRowSelectionChange = useCallback(
    (updater: React.SetStateAction<Record<string, boolean>>) => {
      const newSelection = typeof updater === "function" ? updater(rowSelection) : updater;
      const newSelectedItems = Object.entries(newSelection)
        .filter(([, selected]) => selected)
        .map(([key]) => data[Number(key)])
        .filter(Boolean) as IMetaDetails[];
      setSelectedItems(newSelectedItems);
    },
    [rowSelection, data, setSelectedItems]
  );

  // Server-side sorting state
  const [sorting, setSorting] = useState<MRT_SortingState>([]);

  const handleSortingChange = useCallback(
    (updater: React.SetStateAction<MRT_SortingState>) => {
      setSorting((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        if (onSortingChange) {
          if (next.length === 0) {
            onSortingChange(null);
          } else {
            const { id, desc } = next[0];
            const serverColumn = SORT_COLUMN_MAP[id];
            if (serverColumn) {
              onSortingChange({ sort_by: serverColumn, sort: desc ? "desc" : "asc" });
            } else {
              onSortingChange(null);
            }
          }
        }
        return next;
      });
    },
    [onSortingChange]
  );

  const isDetailNotSyncedFile = (detail: IMetaDetails): boolean => {
    return (
      (detail.entity_type === EntityType.FILE &&
        (detail as IFileMetaDetail).dataroom_file_id &&
        (detail as IFileMetaDetail).file_url === "") ||
      false
    );
  };

  const columns = useMemo<MRT_ColumnDef<IMetaDetails>[]>(
    () => [
      {
        id: "indicators",
        header: "",
        size: 16,
        minSize: 16,
        maxSize: 16,
        enableSorting: false,
        enableColumnActions: false,
        enableResizing: false,
        enableHiding: false,
        enableColumnOrdering: false,
        enableColumnDragging: false,
        muiTableHeadCellProps: {
          sx: {
            flex: "0 0 16px !important",
            minWidth: "16px !important",
            width: "16px !important",
            padding: "0 !important",
            borderBottom: "1px solid #dbe8f5"
          }
        },
        muiTableBodyCellProps: {
          sx: {
            flex: "0 0 16px !important",
            minWidth: "16px !important",
            width: "16px !important",
            padding: "0 !important",
            borderBottom: "1px solid #dbe8f5"
          }
        },
        Cell: ({ cell }) => {
          const row = cell.row.original;
          const isAi = (row as IFileMetaDetail).has_ai_tags || (row as IFormMetaDetail).is_ai;
          return (
            <Stack direction="row" alignItems="center" justifyContent="center" gap="10px">
              <Box sx={{ width: 16, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {Boolean(isAi) && <AutoAwesomeOutlinedIcon sx={{ fontSize: 16, color: "#c09560" }} />}
              </Box>
            </Stack>
          );
        }
      },
      {
        id: "name",
        header: "Name",
        Header: () => (
          <MaterialTooltip title="Name" isShow>
            <Typography noWrap>Name</Typography>
          </MaterialTooltip>
        ),
        accessorFn: (row) => ("name" in row ? (row as IFileMetaDetail).name : (row as IFolderMetaDetail).title) || "",
        size: 240,
        minSize: 180,
        Cell: ({ cell }) => {
          const row = cell.row.original;
          const displayName = "name" in row ? row.name : "title" in row ? row.title : "";
          return (
            <MaterialTooltip title={displayName} isShow>
              <Stack
                direction="row"
                alignItems="center"
                gap="6px"
                onClick={() => onRowClick?.(row)}
                sx={{ overflow: "hidden", cursor: onRowClick ? "pointer" : "default" }}
              >
                {isDetailNotSyncedFile(row) ? (
                  <Box sx={{ position: "relative" }}>
                    <FileIcon type={row.type} style={{ fontSize: 16, fill: "black", flexShrink: 0, opacity: 0.6 }} />
                    <SyncIcon
                      sx={{
                        borderRadius: "10px",
                        backgroundColor: "white",
                        position: "absolute",
                        fontSize: "13px",
                        bottom: "4px",
                        right: "0px"
                      }}
                    />
                  </Box>
                ) : (
                  <>
                    <FileIcon type={row.type} style={{ fontSize: 16, fill: "black", flexShrink: 0 }} />
                    {row.is_private && <VisibilityLock sx={{ fill: "#BAC7CE" }} />}
                  </>
                )}

                <Typography
                  noWrap
                  sx={{
                    fontSize: 14,
                    color: "#071f35",
                    lineHeight: "16px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "block"
                  }}
                >
                  {displayName}
                </Typography>
              </Stack>
            </MaterialTooltip>
          );
        }
      },
      ...(showDealColumn
        ? [
            {
              id: "deal",
              header: "Deal",
              Header: () => (
                <MaterialTooltip title="Deal" isShow>
                  <Typography noWrap>Deal</Typography>
                </MaterialTooltip>
              ),
              accessorFn: (row: IMetaDetails) => {
                const deal = "deal" in row ? (row as IFileMetaDetail).deal : null;
                return deal?.title || "";
              },
              size: 160,
              minSize: 100,
              Cell: ({ row }: { row: MRT_Row<IMetaDetails> }) => {
                const deal = "deal" in row.original ? (row.original as IFileMetaDetail).deal : null;
                return <DealCell value={deal?.title || ""} dealId={deal?.id ?? null} />;
              }
            } as MRT_ColumnDef<IMetaDetails>
          ]
        : []),
      {
        id: "altius_file_classification",
        header: " Altius Classification",
        Header: () => (
          <MaterialTooltip title="Altius Classification" isShow>
            <Typography noWrap>Altius Classification</Typography>
          </MaterialTooltip>
        ),
        accessorFn: (row) => (row as IDataroomFileDetail).document_type || "",
        size: 220,
        minSize: 100,
        Cell: ({ row }: { row: MRT_Row<IMetaDetails> }) => {
          const entityType = (row.original as IFileMetaDetail).entity_type;
          const documentType = (row.original as IFileMetaDetail).document_type ?? null;

          if (entityType !== EntityType.FILE || (isReadOnly && !documentType)) {
            return <TextCell value="" />;
          }
          if (!documentType) {
            return <AddValueButton onClick={(e) => handleClassificationUpdate(e, row.original.id, null)} />;
          }
          return (
            <EditableCell
              disabled={isReadOnly}
              value={documentType}
              onClick={(e) => handleClassificationUpdate(e, row.original.id, documentType)}
            />
          );
        }
      },
      ...(kind === "dataroom" || showDataroomColumns
        ? [
            {
              id: "account_name",
              header: "Cloud Account Name",
              Header: () => (
                <MaterialTooltip title="Cloud Account Name" isShow>
                  <Typography noWrap>Cloud Account Name</Typography>
                </MaterialTooltip>
              ),
              accessorKey: "account_name",
              size: 250,
              minSize: 100,
              Cell: ({ row }: { row: MRT_Row<IMetaDetails> }) => (
                <TextCell value={(row.original as IDataroomFileDetail).account_name || ""} />
              )
            } as MRT_ColumnDef<IMetaDetails>,
            {
              id: "dataroom_classification",
              header: "Cloud Classification",
              Header: () => (
                <MaterialTooltip title="Cloud Classification" isShow>
                  <Typography noWrap>Cloud Classification</Typography>
                </MaterialTooltip>
              ),
              accessorFn: (row) => (row as IDataroomFileDetail).cloud_classification || "",
              size: 250,
              minSize: 100,
              Cell: ({ row }: { row: MRT_Row<IMetaDetails> }) => (
                <TextCell value={(row.original as IDataroomFileDetail).cloud_classification || ""} />
              )
            } as MRT_ColumnDef<IMetaDetails>,
            {
              id: "dataroom_sync_date",
              header: "Cloud Sync Date",
              Header: () => (
                <MaterialTooltip title="Cloud Sync Date" isShow>
                  <Typography noWrap>Cloud Sync Date</Typography>
                </MaterialTooltip>
              ),
              accessorFn: (row) => (row as IDataroomFileDetail).downloaded_at || "",
              size: 250,
              minSize: 100,
              Cell: ({ row }: { row: MRT_Row<IMetaDetails> }) => {
                const value = (row.original as IDataroomFileDetail).downloaded_at || "";
                const normalized = value ? formatDate({ date: value, format: "MM/DD/YYYY" }) : "";
                return <TextCell value={normalized} />;
              }
            } as MRT_ColumnDef<IMetaDetails>,
            {
              id: "dataroom_index_date",
              header: "Cloud Index Date",
              Header: () => (
                <MaterialTooltip title="Cloud Index Date" isShow>
                  <Typography noWrap>Cloud Index Date</Typography>
                </MaterialTooltip>
              ),
              accessorFn: (row) => (row as IDataroomFileDetail).created_at || "",
              size: 250,
              minSize: 100,
              Cell: ({ row }: { row: MRT_Row<IMetaDetails> }) => {
                const value = (row.original as IDataroomFileDetail).created_at || "";
                const normalized = value ? formatDate({ date: value, format: "MM/DD/YYYY" }) : "";
                return <TextCell value={(row.original as IFileMetaDetail)?.source === "Dataroom" ? normalized : ""} />;
              }
            } as MRT_ColumnDef<IMetaDetails>,
            {
              id: "dataroom_effective_date",
              header: "Cloud Effective Date",
              Header: () => (
                <MaterialTooltip title="Cloud Effective Date" isShow>
                  <Typography noWrap>Cloud Effective Date</Typography>
                </MaterialTooltip>
              ),
              accessorFn: (row) => (row as IDataroomFileDetail).effective_at || "",
              size: 250,
              minSize: 100,
              Cell: ({ row }: { row: MRT_Row<IMetaDetails> }) => {
                const value = (row.original as IDataroomFileDetail).effective_at || "";
                const normalized = value ? formatDate({ date: value, format: "MM/DD/YYYY" }) : "";
                return <TextCell value={normalized} />;
              }
            } as MRT_ColumnDef<IMetaDetails>
          ]
        : []),
      {
        id: "investor",
        header: "Investor",
        Header: () => (
          <MaterialTooltip title="Investor" isShow>
            <Typography noWrap>Investor</Typography>
          </MaterialTooltip>
        ),
        accessorFn: (row) => (row as IFileMetaDetail).investor?.name || "",
        size: 140,
        minSize: 100,
        Cell: ({ row }) => {
          const entityType = row.original.entity_type;
          const fileMeta = row.original as IFileMetaDetail;
          const investor = fileMeta.investor || null;
          const investorIds = fileMeta.investor_ids ?? [];
          const isAllInvestors = fileMeta.all_investors === true;

          const handleClick = (e: React.MouseEvent<HTMLDivElement>) =>
            handleInvestorUpdate({
              e,
              fileId: row.original.id,
              currentInvestorId: investor?.id ?? null,
              dealId: fileMeta.deal?.id ?? undefined,
              allInvestors: isAllInvestors,
              investorIds,
              entireCommitment: fileMeta.entire_commitment
            });

          if (entityType !== EntityType.FILE || (isReadOnly && !investor && !isAllInvestors && !investorIds.length)) {
            return <TextCell value="" />;
          }

          if (isAllInvestors) {
            return <EditableCell disabled={isReadOnly} value="All Investors" onClick={handleClick} />;
          }

          if (investorIds.length) {
            const groupNames = getGroupNames(investorIds);
            if (groupNames) {
              return <EditableCell disabled={isReadOnly} value={groupNames} onClick={handleClick} />;
            }
          }
          if (!investor && !investorIds.length && !isAllInvestors) {
            return <AddValueButton onClick={handleClick} />;
          }

          return <EditableCell disabled={isReadOnly} value={investor?.name || ""} onClick={handleClick} />;
        }
      },
      {
        id: "file_status",
        header: "Status",
        Header: () => (
          <MaterialTooltip title="Status" isShow>
            <Typography noWrap>Status</Typography>
          </MaterialTooltip>
        ),
        accessorKey: "file_status",
        size: 150,
        minSize: 100,
        Cell: ({ row }) => {
          if (row.original.entity_type !== EntityType.FILE) {
            return <TextCell value="" />;
          }
          const status = (row.original as IFileMetaDetail).file_status || null;
          const { label, bgcolor, iconColor, Icon } = getStatusConfig(status);
          return (
            <MaterialTooltip title={status}>
              <Chip
                disabled={isReadOnly}
                size="small"
                icon={<Icon sx={{ fontSize: 14, color: `${iconColor} !important` }} />}
                label={label}
                onClick={(e) => handleOperationsStatusUpdate(e, row.original.id, status)}
                sx={{
                  bgcolor,
                  color: "#071f35",
                  fontSize: "12px",
                  height: "24px",
                  cursor: "pointer",
                  maxWidth: "100%",
                  "&:hover": { opacity: 0.85 }
                }}
              />
            </MaterialTooltip>
          );
        }
      },
      {
        id: "tags",
        header: "Tags",
        Header: () => (
          <MaterialTooltip title="Tags" isShow>
            <Typography noWrap>Tags</Typography>
          </MaterialTooltip>
        ),
        accessorKey: "tags",
        size: 140,
        minSize: 100,
        Cell: ({ row }) => {
          const original = row.original;
          const tags = (original as IFileMetaDetail).entity_tag_data?.map((tag) => tag.name) || [];
          const entityType = ((): SyncEntityTagsData["entity_type"] => {
            if (original.type === EntityType.FOLDER) return "section";
            if (original.type === EntityType.DOCUMENT) return "document";
            if (original.type === EntityType.FORM) return "form";
            return "file";
          })();
          return (
            <TagsCell
              tags={tags}
              entityType={entityType}
              entityId={original.id}
              disabled={isReadOnly}
              onTagsUpdated={handleTagsSync}
            />
          );
        }
      },

      ...(hideShareColumn
        ? []
        : [
            {
              id: "shared_with",
              header: "Shared With",
              Header: () => (
                <MaterialTooltip title="Shared With" isShow>
                  <Typography noWrap>Shared With</Typography>
                </MaterialTooltip>
              ),
              accessorKey: "shared_with",
              size: 160,
              minSize: 100,
              Cell: ({ row }: { row: MRT_Row<IMetaDetails> }) => {
                const users = (row.original as IFileMetaDetail).shared_with || [];
                const entityName =
                  "name" in row.original
                    ? (row.original as IFileMetaDetail).name
                    : (row.original as IFolderMetaDetail).title;
                const dealId = (row.original as IFileMetaDetail).deal?.id ?? null;
                const isDataroomFile = (row.original as IFileMetaDetail).source === "Dataroom";
                const isDataroomFolder = (row.original as IFolderMetaDetail).kind === "dataroom";

                const isShareAvailable = !isDataroomFile && !isDataroomFolder && dealId && !isReadOnly;

                let shareType: "section_id" | "form_id" | "document_id" | "file_id" = "file_id";
                if (row.original.type === EntityType.FOLDER) shareType = "section_id";
                else if (row.original.type === EntityType.FORM) shareType = "form_id";
                else if (row.original.type === EntityType.DOCUMENT) shareType = "document_id";

                return (
                  <Box
                    onClick={(e) =>
                      isShareAvailable && handleShareUpdate(e, row.original.id, entityName, dealId, shareType)
                    }
                    sx={{
                      cursor: !isShareAvailable ? "default" : "pointer",
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center"
                    }}
                  >
                    {users.length ? <SharedCell users={users} /> : <TextCell value="" />}
                  </Box>
                );
              }
            }
          ]),
      {
        id: "type",
        header: "Type",
        Header: () => (
          <MaterialTooltip title="Type" isShow>
            <Typography noWrap>Type</Typography>
          </MaterialTooltip>
        ),
        accessorKey: "type",
        size: 160,
        minSize: 100,
        Cell: ({ row }) => {
          const value = row.original.type;
          return row.original.entity_type === EntityType.FOLDER ? (
            <TextCell value={""} />
          ) : (
            <TextCell value={String(value || "").toUpperCase()} />
          );
        }
      },
      {
        id: "last_update",
        header: "Last Update",
        Header: () => (
          <MaterialTooltip title="Last Update" isShow>
            <Typography noWrap>Last Update</Typography>
          </MaterialTooltip>
        ),
        accessorFn: (row) => (row as IFileMetaDetail).updated_at || "",
        size: 180,
        minSize: 100,
        Cell: ({ row }) => {
          const value = (row.original as IFileMetaDetail).updated_at || "";
          const normalized = value ? formatDate({ date: value, format: "MM/DD/YYYY" }) : "";
          return <TextCell value={normalized} />;
        }
      },
      {
        id: "effective_date",
        header: "Effective Date",
        Header: () => (
          <MaterialTooltip title="Effective Date" isShow>
            <Typography noWrap>Effective Date</Typography>
          </MaterialTooltip>
        ),
        accessorKey: "effective_date",
        size: 200,
        minSize: 100,
        Cell: ({ row }) => {
          const entityType = (row.original as IFileMetaDetail).entity_type;
          const effectiveDate = (row.original as IFileMetaDetail).effective_date ?? null;
          if (entityType !== EntityType.FILE || (isReadOnly && !effectiveDate)) {
            return <TextCell value="" />;
          }
          if (!effectiveDate) {
            return <AddValueButton onClick={(e) => handleEffectiveDateUpdate(e, row.original.id, null)} />;
          }
          return (
            <EditableCell
              disabled={isReadOnly}
              value={formatDate({ date: effectiveDate, format: "DD/MM/YYYY" })}
              onClick={(e) => handleEffectiveDateUpdate(e, row.original.id, effectiveDate)}
            />
          );
        }
      },
      {
        id: "currency",
        header: "Currency",
        Header: () => (
          <MaterialTooltip title="Currency" isShow>
            <Typography noWrap>Currency</Typography>
          </MaterialTooltip>
        ),
        accessorKey: "currency",
        size: 140,
        minSize: 100,
        Cell: ({ row }) => {
          const entityType = (row.original as IFileMetaDetail).entity_type;
          const currency = (row.original as IFileMetaDetail).currency ?? null;
          if (entityType !== EntityType.FILE || (isReadOnly && !currency)) {
            return <TextCell value="" />;
          }
          if (!currency) {
            return <AddValueButton onClick={(e) => handleCurrencyUpdate(e, row.original.id, null)} />;
          }
          return (
            <EditableCell
              disabled={isReadOnly}
              value={currency}
              onClick={(e) => handleCurrencyUpdate(e, row.original.id, currency)}
            />
          );
        }
      },
      {
        id: "source",
        header: "Source",
        Header: () => (
          <MaterialTooltip title="Source" isShow>
            <Typography noWrap>Source</Typography>
          </MaterialTooltip>
        ),
        accessorKey: "source",
        size: 140,
        minSize: 100,
        Cell: ({ row }) => {
          const source = (row.original as IFileMetaDetail).source;
          if (!source) {
            return <TextCell value="" />;
          }
          return (
            <MaterialTooltip title={source}>
              <Chip label={source} size="small" />
            </MaterialTooltip>
          );
        }
      },
      {
        id: "upload_date",
        header: "Upload Date",
        Header: () => (
          <MaterialTooltip title="Upload Date" isShow>
            <Typography noWrap>Upload Date</Typography>
          </MaterialTooltip>
        ),
        accessorFn: (row) => (row as IFileMetaDetail).created_at || "",
        size: 180,
        minSize: 100,
        Cell: ({ row }) => {
          const value = (row.original as IFileMetaDetail).created_at || "";
          const normalized = value ? formatDate({ date: value, format: "MM/DD/YYYY" }) : "";
          return <TextCell value={normalized} />;
        }
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        enableColumnActions: false,
        enableResizing: false,
        enableColumnOrdering: false,
        enablePinning: true,
        size: 110,
        minSize: 110,
        maxSize: 110,
        muiTableHeadCellProps: {
          sx: {
            padding: "0 !important",
            backgroundColor: "transparent !important",
            borderBottom: "1px solid #dbe8f5 !important",
            "&::before": {
              display: "none"
            }
          }
        },
        muiTableBodyCellProps: {
          className: "pinned-actions-cell",
          sx: {
            padding: "0 !important",
            position: "sticky",
            right: 0,
            zIndex: 1,
            backgroundColor: "white",
            borderBottom: "1px solid #dbe8f5",
            "tr:last-child &": {
              borderBottom: "none"
            },
            "&::before": {
              display: "none"
            }
          }
        },
        Cell: ({ row }) => {
          const { type } = row.original;

          const content = (() => {
            if (type === EntityType.FORM) {
              return (
                <FormActionsComponent row={row.original as IFormMetaDetail} refetchHandler={refetchFolderMetadata} />
              );
            }
            if (type === EntityType.DOCUMENT) {
              return (
                <DocumentActionsComponent
                  row={row.original as IDocumentMetaDetail}
                  refetchHandler={refetchFolderMetadata}
                />
              );
            }
            if (type === EntityType.FOLDER) {
              return (
                <FolderActionsComponent
                  folder={row.original as IFolderMetaDetail}
                  refetchHandler={refetchFolderMetadata}
                />
              );
            }

            return (
              <FileActionsComponent row={row.original as IFileMetaDetail} refetchHandler={refetchFolderMetadata} />
            );
          })();

          return (
            <Box
              className="pinned-actions-content"
              onClick={(e) => e.stopPropagation()}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                height: "100%",
                px: "8px",
                opacity: 0,
                pointerEvents: "none",
                transition: "opacity 0.15s"
              }}
            >
              {content}
            </Box>
          );
        }
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [kind, showDealColumn, hideShareColumn, showDataroomColumns]
  );

  const defaultColumnOrder = useMemo(
    () => ["mrt-row-select", ...columns.map((c) => c.id as string), "actions"],
    [columns]
  );

  const mrtColumnOrder = columnOrder?.length ? columnOrder : defaultColumnOrder;

  const handleColumnOrderChange = useCallback(
    (updater: React.SetStateAction<string[]>) => {
      const newOrder = typeof updater === "function" ? updater(mrtColumnOrder) : updater;
      reorderColumns(newOrder);
    },
    [mrtColumnOrder, reorderColumns]
  );

  const table = useMaterialReactTable<IMetaDetails>({
    columns,
    data,
    layoutMode: "grid",
    enableColumnPinning: true,
    state: {
      isLoading,
      columnVisibility,
      rowSelection,
      columnOrder: mrtColumnOrder,
      columnPinning: { right: ["actions"] },
      ...(manualSorting ? { sorting } : {})
    },
    onRowSelectionChange: handleRowSelectionChange,
    onColumnOrderChange: handleColumnOrderChange,
    ...(manualSorting ? { manualSorting: true, onSortingChange: handleSortingChange } : {}),
    mrtTheme: { selectedRowBackgroundColor: "#ffffff" },
    enableColumnActions: false,
    enableColumnFilters: false,
    enableColumnResizing: true,
    enableColumnOrdering: true,
    icons: { DragHandleIcon: () => <DragIndicatorIcon sx={{ fontSize: 16, color: "#92a7bc" }} /> },
    enablePagination: false,
    enableSorting: true,
    sortDescFirst: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableRowSelection: enableRowSelection,
    enableStickyHeader: true,
    enableRowVirtualization: true,

    rowVirtualizerOptions: {
      estimateSize: () => 32,
      overscan: 12,
      initialRect: { width: 0, height: 480 }
    },
    columnResizeMode: "onChange",
    muiTablePaperProps: {
      sx: {
        boxShadow: "none",
        border: "1px solid #dbe8f5",
        borderRadius: "8px",
        overflow: "hidden",
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        "& thead th .MuiIconButton-root": { opacity: "0 !important", transition: "opacity 0.15s" },
        "& thead th:hover .MuiIconButton-root": { opacity: "0.7 !important" },
        "& thead th .MuiTableSortLabel-root": { opacity: "0 !important", transition: "opacity 0.15s" },
        "& thead th:hover .MuiTableSortLabel-root": { opacity: "1 !important" },
        "& thead th[data-sort] .MuiTableSortLabel-root": { opacity: "1 !important" }
      }
    },
    muiTableProps: {
      sx: { paddingBottom: "15px" }
    },
    muiTableContainerProps: {
      sx: { flex: 1, minHeight: 0, overflow: "auto", backgroundColor: "white" },
      onScroll: onFetchNextPage
        ? (e: React.UIEvent<HTMLDivElement>) => {
            const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
            if (scrollHeight - scrollTop - clientHeight < 300) {
              onFetchNextPage();
            }
          }
        : undefined
    },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: "#f6f8fc",
        color: "#071f35",
        fontSize: 14,
        fontWeight: 500,
        height: 32,
        minHeight: 32,
        maxHeight: 32,
        py: "4px",
        px: "16px",
        borderBottom: "1px solid #dbe8f5",
        overflow: "visible",
        "& .Mui-TableHeadCell-Content-Wrapper": {
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          "&:hover": { textOverflow: "ellipsis" }
        },
        "& .MuiDivider-root:not(.Mui-TableHeadCell-ResizeHandle-Divider)": { display: "none" },
        "& .Mui-TableHeadCell-ResizeHandle-Wrapper": {
          cursor: "col-resize",
          position: "absolute",
          paddingLeft: "0px",
          right: "0px !important",
          borderColor: "#e6e8f2 !important"
        },
        "& .Mui-TableHeadCell-ResizeHandle-Divider": {
          borderColor: "#e6e8f2 !important",
          opacity: 1,
          borderWidth: "1px !important"
        },
        "&::before": { display: "none" }
      }
    },
    muiTableHeadRowProps: {
      sx: {
        boxShadow: "none",
        height: "32px",
        minHeight: "32px",
        "& th": { height: "32px", minHeight: "32px" }
      }
    },
    muiTableBodyCellProps: {
      sx: {
        backgroundColor: "white",
        fontSize: 14,
        color: "#071f35",
        borderBottom: "1px solid #dbe8f5",
        height: 32,
        minHeight: 32,
        maxHeight: 32,
        py: "8px",
        px: "16px",
        verticalAlign: "middle",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        overflow: "hidden",
        "tr:hover &": { backgroundColor: "#ecf1f8 !important" },
        "tr:has([aria-expanded='true']) &": { backgroundColor: "#ecf1f8 !important" },
        "&.Mui-selected": { backgroundColor: "white !important" },
        "&::before": { display: "none" }
      }
    },
    muiTableBodyRowProps: ({ row }) => {
      const drag = getRowDragHandlers?.(row.original) ?? {
        isHighlighted: false,
        draggable: false,
        onDragStart: undefined,
        onDragEnd: undefined,
        onDragOver: undefined,
        onDragLeave: undefined,
        onDrop: undefined
      };
      const { isHighlighted, draggable, onDragStart, onDragEnd, onDragOver, onDragLeave, onDrop } = drag;
      const isHighlightedFromExtractor = highlightedItemId === row.original.id;

      return {
        ...tableBodyRowProps,
        draggable,
        onDragStart,
        onDragEnd,
        onDragOver,
        onDragLeave,
        onDrop,
        sx: {
          ...tableBodyRowProps.sx,
          cursor: "default",
          transition: "background-color 0.3s ease",
          "& td": {
            ...tableBodyRowProps.sx["& td"],
            transition: "background-color 0.3s ease"
          },
          ...(isHighlightedFromExtractor && {
            backgroundColor: "#ecf1f8 !important",
            "& td": {
              ...tableBodyRowProps.sx["& td"],
              backgroundColor: "#ecf1f8 !important",
              transition: "background-color 0.3s ease"
            },
            "& td::after": { backgroundColor: "#ecf1f8" },
            "& .row-hover-actions": { opacity: 1 },
            "& .pinned-actions-content": { opacity: 1, pointerEvents: "auto" as const }
          }),
          ...(isHighlighted
            ? {
                "& td": {
                  ...tableBodyRowProps.sx["& td"],
                  backgroundColor: "#ecf1f8 !important"
                },
                outline: "2px dashed #5B9BD5",
                outlineOffset: "-2px"
              }
            : {})
        }
      };
    },
    muiSelectCheckboxProps: {
      size: "small",
      disableRipple: true,
      sx: { p: 0, m: 0, width: "1.25rem", height: "1.25rem" }
    },
    muiSelectAllCheckboxProps: {
      size: "small",
      disableRipple: true,
      sx: { p: 0, m: 0, width: "1.25rem", height: "1.25rem" }
    },
    displayColumnDefOptions: {
      "mrt-row-select": {
        enableResizing: false,
        size: 32,
        minSize: 32,
        maxSize: 32,
        muiTableHeadCellProps: {
          sx: {
            backgroundColor: "#f6f8fc",
            borderBottom: "1px solid #dbe8f5",
            width: 32,
            minWidth: 32,
            maxWidth: 32,
            py: "4px",
            px: "0px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "& .Mui-TableHeadCell-Content": {
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%"
            },
            "& .Mui-TableHeadCell-Content-Wrapper": {
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            },
            "& .MuiDivider-root": { display: "none" }
          }
        },
        muiTableBodyCellProps: {
          sx: {
            width: 32,
            minWidth: 32,
            maxWidth: 32,
            py: "8px",
            px: "0px",
            borderBottom: "1px solid #dbe8f5",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }
        }
      }
    }
  });

  return <MaterialReactTable table={table} />;
};
